import { defaultDataSourceConfigs } from '../data/dataSources'
import { loadDataSourceConfigs } from '../storage/localConfig'
import { classifyPredictionCategory } from '../utils/category'
import type { DataSourceId } from '../types/dataSource'
import type { PredictionCategory } from '../types/prediction'
import type {
  EventCatalogChunk,
  EventCatalogManifest,
  PredictionEventCatalog,
} from '../types/event'
import { isDeadlinePassed } from '../utils/freshness'

const EVENT_INDEX_PATH = '/data/events/index.json'
const EVENT_FEATURED_PATH = '/data/events/featured.json'
const EVENT_SEARCH_MANIFEST_PATH = '/data/events/search/index.json'
const SOURCE_IDS = ['polymarket', 'kalshi'] as const
const MACRO_PRESENTATION_SIGNAL = /\b(?:inflation|cpi|gdp|interest rate|unemployment|employment|payroll|jobs report|consumer price|producer price|central bank|federal reserve|fed funds|recession|monetary policy|treasury|bond yield|economic growth|economy)\b|通胀|消费价格|生产者价格|失业率|就业|国内生产总值|利率|央行|美联储|经济增长|货币政策/u

export type EventSourceStatus = 'success' | 'empty' | 'error' | 'disabled'

export interface EventSourceResult {
  id: Exclude<DataSourceId, 'cme-fedwatch' | 'mock'>
  name: 'Polymarket' | 'Kalshi'
  status: EventSourceStatus
  events: PredictionEventCatalog[]
  eventCount: number
  marketCount: number
  updatedAt?: string
  error?: string
}

export interface HomeEventFeed {
  events: PredictionEventCatalog[]
  sources: EventSourceResult[]
  fetchedAt: string
  hasMore: boolean
  nextPage: number
}

interface EventSearchItem {
  id: string
  title: string
  originalTitle?: string
  category: PredictionCategory
  sources: string[]
  path: string
  position: number
}

interface EventSearchManifest {
  chunks: string[]
}

let manifestPromise: Promise<EventCatalogManifest> | null = null
let featuredPromise: Promise<PredictionEventCatalog[]> | null = null
let searchPromise: Promise<EventSearchItem[]> | null = null
let resolvedCategoryIndexPromise: Promise<Map<PredictionCategory, EventSearchItem[]>> | null = null
type EventLoadMode = 'display' | 'headline' | 'catalog'

const chunkPromises = new Map<string, Promise<PredictionEventCatalog[]>>()
const rawChunkPromises = new Map<string, Promise<PredictionEventCatalog[]>>()

async function fetchJson<T>(path: string, options: { forceRefresh?: boolean } = {}) {
  const response = await fetch(path, {
    cache: options.forceRefresh ? 'no-store' : 'default',
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw new Error(`Event 目录数据请求失败（${response.status}）`)
  return response.json() as Promise<T>
}

function isBrowsable(event: PredictionEventCatalog, mode: EventLoadMode = 'display') {
  return Boolean(event.title)
    && event.status === 'open'
    && !isDeadlinePassed(event.deadline)
    && (mode === 'catalog' || (mode === 'headline' ? event.headlineEligible === true : event.displayEligible === true))
}

function sourceEnabled(id: (typeof SOURCE_IDS)[number]) {
  if (typeof window === 'undefined') return true
  const configs = loadDataSourceConfigs(defaultDataSourceConfigs)
  return configs.find((config) => config.id === id)?.enabled ?? true
}

function sourceIdForName(source: string): (typeof SOURCE_IDS)[number] {
  return source.toLowerCase() === 'polymarket' ? 'polymarket' : 'kalshi'
}

function eventSemanticTexts(event: PredictionEventCatalog) {
  return [
    event.originalTitle,
    event.title,
    event.nativeEventTitle,
    event.nativeSeriesTitle,
  ]
}

function resolvePresentationCategory(rawCategory: PredictionCategory, ...texts: unknown[]) {
  const semanticText = texts.flatMap((value) => Array.isArray(value) ? value : [value])
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .toLowerCase()
  if (MACRO_PRESENTATION_SIGNAL.test(semanticText)) return 'finance' as const
  return classifyPredictionCategory(rawCategory, ...texts)
}

function toPresentationEvent(event: PredictionEventCatalog): PredictionEventCatalog {
  const rawCategory = event.category
  const displayCategory = resolvePresentationCategory(rawCategory, ...eventSemanticTexts(event))
  return {
    ...event,
    rawCategory,
    resolvedCategory: displayCategory,
    displayCategory,
    category: displayCategory,
  }
}

function resolvedSearchCategory(item: EventSearchItem) {
  return resolvePresentationCategory(item.category, item.originalTitle, item.title, item.sources)
}

function loadEventChunk(path: string, mode: EventLoadMode = 'display') {
  const cacheKey = `${mode}:${path}`
  const cached = chunkPromises.get(cacheKey)
  if (cached) return cached
  const promise = fetchJson<EventCatalogChunk>(path)
    .then((payload) => (payload.events ?? [])
      .filter((event) => isBrowsable(event, mode))
      .map(toPresentationEvent))
    .catch((error) => {
      chunkPromises.delete(cacheKey)
      throw error
    })
  chunkPromises.set(cacheKey, promise)
  return promise
}

export function loadEventCatalogManifest(options: { forceRefresh?: boolean } = {}) {
  if (options.forceRefresh || manifestPromise === null) {
    manifestPromise = fetchJson<EventCatalogManifest>(EVENT_INDEX_PATH, options)
  }
  return manifestPromise
}

async function loadFeaturedEvents(options: { forceRefresh?: boolean } = {}) {
  if (options.forceRefresh || featuredPromise === null) {
    featuredPromise = fetchJson<{ events: PredictionEventCatalog[] }>(EVENT_FEATURED_PATH, options)
      .then((payload) => (payload.events ?? [])
        .filter((event) => isBrowsable(event, 'headline'))
        .map(toPresentationEvent))
  }
  return featuredPromise
}

async function loadEventCategoryPage(
  category: PredictionCategory,
  page = 0,
  options: { forceRefresh?: boolean } = {},
) {
  const manifest = await loadEventCatalogManifest(options)
  const categoryManifest = manifest.categories[category]
  if (!categoryManifest) return { events: [], page, hasMore: false, total: 0 }

  // Keep the formal chunk paths immutable.  Category navigation is resolved in
  // this presentation layer so a stale raw bucket cannot decide the public
  // category or filter.
  const resolvedIndex = await loadResolvedCategoryIndex(options)
  const categoryItems = resolvedIndex.get(category) ?? []
  const paths = [...new Set(categoryItems.map((item) => item.path))]
  const chunks = await Promise.all(paths.map(async (chunkPath) => [chunkPath, await loadEventChunk(chunkPath, 'catalog')] as const))
  const eventsById = new Map(chunks.flatMap(([, events]) => events.map((event) => [event.id, event] as const)))
  const events = categoryItems
    .slice()
    .sort((left, right) => left.position - right.position)
    .map((item) => eventsById.get(item.id))
    .filter((event): event is PredictionEventCatalog => Boolean(event && event.category === category))
  return {
    // The presentation service owns the 30-item UI page.  This layer returns
    // the full resolved category so pagination cannot stop at a display-only
    // subset or at an arbitrary intermediate cap.
    events,
    page,
    hasMore: false,
    total: events.length,
  }
}

async function loadSearchIndex(options: { forceRefresh?: boolean } = {}) {
  if (options.forceRefresh || searchPromise === null) {
    searchPromise = fetchJson<EventSearchManifest>(EVENT_SEARCH_MANIFEST_PATH, options)
      .then(async (manifest) => {
        const chunks = await Promise.all((manifest.chunks ?? []).map((path) => fetchJson<{ items: EventSearchItem[] }>(path, options)))
        return chunks.flatMap((payload) => payload.items ?? [])
      })
  }
  return searchPromise
}

export async function loadEventsByIds(
  ids: string[],
  options: { forceRefresh?: boolean } = {},
) {
  const requestedIds = [...new Set(ids.filter(Boolean))]
  if (requestedIds.length === 0) return []

  const searchItems = await loadSearchIndex(options)
  const requested = new Set(requestedIds)
  const matches = searchItems.filter((item) => requested.has(item.id))
  const paths = [...new Set(matches.map((item) => item.path))]
  const chunks = await Promise.all(paths.map(async (path) => {
    const cached = rawChunkPromises.get(path)
    if (cached) return [path, await cached] as const
    const promise = fetchJson<EventCatalogChunk>(path, options)
      .then((payload) => (payload.events ?? [])
        .filter((event) => Boolean(event.id) && event.status === 'open' && Boolean(event.title))
        .map(toPresentationEvent))
      .catch((error) => {
        rawChunkPromises.delete(path)
        throw error
      })
    rawChunkPromises.set(path, promise)
    return [path, await promise] as const
  }))
  const eventsById = new Map(chunks.flatMap(([, events]) => events.map((event) => [event.id, event] as const)))

  return requestedIds
    .map((id) => eventsById.get(id))
    .filter((event): event is PredictionEventCatalog => Boolean(event))
}

async function loadResolvedCategoryIndex(options: { forceRefresh?: boolean } = {}) {
  if (options.forceRefresh || resolvedCategoryIndexPromise === null) {
    resolvedCategoryIndexPromise = loadSearchIndex(options).then((items) => {
      const index = new Map<PredictionCategory, EventSearchItem[]>()
      for (const item of items) {
        const category = resolvedSearchCategory(item)
        const bucket = index.get(category) ?? []
        bucket.push(item)
        index.set(category, bucket)
      }
      return index
    })
  }
  return resolvedCategoryIndexPromise
}

async function searchEvents(query: string, options: { forceRefresh?: boolean } = {}) {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return loadFeaturedEvents(options)
  const searchItems = await loadSearchIndex(options)
  const matches = searchItems.filter((item) => `${item.title} ${item.originalTitle ?? ''} ${item.sources.join(' ')} ${item.category} ${resolvedSearchCategory(item)}`.toLowerCase().includes(normalizedQuery))
  const paths = [...new Set(matches.map((item) => item.path))]
  const chunks = await Promise.all(paths.map(async (path) => [path, await loadEventChunk(path, 'catalog')] as const))
  const eventsById = new Map(chunks.flatMap(([, events]) => events.map((event) => [event.id, event] as const)))
  return matches
    .sort((left, right) => left.position - right.position)
    .map((item) => eventsById.get(item.id))
    .filter((event): event is PredictionEventCatalog => Boolean(event))
}

function activityScore(event: PredictionEventCatalog) {
  if (event.activity !== undefined) return event.activity
  const maxima = new Map<string, number>()
  for (const market of event.markets) {
    const unit = market.volume24h !== undefined || market.volume !== undefined
      ? market.volumeUnit
      : market.liquidityUnit
    if (!unit || unit === 'unknown') continue
    const value = market.volume24h ?? market.volume ?? market.liquidity
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) continue
    const key = `${market.source}|${unit}`
    maxima.set(key, Math.max(maxima.get(key) ?? 0, value))
  }
  return event.markets.reduce((sum, market) => {
    const unit = market.volume24h !== undefined || market.volume !== undefined
      ? market.volumeUnit
      : market.liquidityUnit
    const value = market.volume24h ?? market.volume ?? market.liquidity
    if (!unit || unit === 'unknown' || typeof value !== 'number' || !Number.isFinite(value) || value < 0) return sum
    const maximum = maxima.get(`${market.source}|${unit}`) ?? 0
    return maximum > 0 ? sum + Math.log1p(value) / Math.log1p(maximum) : sum
  }, 0)
}

function makeSourceResult(name: 'Polymarket' | 'Kalshi', events: PredictionEventCatalog[], manifest: EventCatalogManifest) {
  const id = sourceIdForName(name)
  const sourceManifest = manifest.sources[name]
  if (!sourceEnabled(id)) {
    return { id, name, status: 'disabled' as const, events: [], eventCount: sourceManifest?.events ?? 0, marketCount: sourceManifest?.markets ?? 0 }
  }
  return {
    id,
    name,
    status: events.length > 0 ? 'success' as const : 'empty' as const,
    events,
    eventCount: sourceManifest?.events ?? events.length,
    marketCount: sourceManifest?.markets ?? 0,
    updatedAt: manifest.generatedAt,
  }
}

export async function getHomeEvents(options: {
  forceRefresh?: boolean
  category?: PredictionCategory | null
  query?: string
  page?: number
} = {}): Promise<HomeEventFeed> {
  const manifest = await loadEventCatalogManifest({ forceRefresh: options.forceRefresh })
  const category = options.category ?? null
  const query = options.query?.trim() ?? ''
  const page = options.page ?? 0
  let events: PredictionEventCatalog[]
  let hasMore = false
  if (query) {
    events = await searchEvents(query, { forceRefresh: options.forceRefresh })
  } else if (category) {
    const result = await loadEventCategoryPage(category, page, { forceRefresh: options.forceRefresh })
    events = result.events
    hasMore = result.hasMore
  } else {
    events = await loadFeaturedEvents({ forceRefresh: options.forceRefresh })
  }
  // The public homepage is a static same-origin presentation. Do not turn a
  // page read into a source refresh or use the browser clock as data time.
  const refreshedEvents = events
  const enabledEvents = refreshedEvents.filter((event) => event.sources.some((source) => sourceEnabled(sourceIdForName(source))))
  const sources = SOURCE_IDS.map((id) => makeSourceResult(
    id === 'polymarket' ? 'Polymarket' : 'Kalshi',
    enabledEvents.filter((event) => event.sources.some((source) => source.toLowerCase() === id)),
    manifest,
  ))
  return {
    events: [...enabledEvents].sort((left, right) => activityScore(right) - activityScore(left)),
    sources,
    fetchedAt: manifest.generatedAt,
    hasMore,
    nextPage: hasMore ? page + 1 : page,
  }
}

/**
 * Phase 05 catalog browsing stays on the existing same-origin Event Catalog.
 * It intentionally skips the older homepage refresh adapter so presentation
 * work does not trigger a new Polymarket/Kalshi fetch.
 */
export async function getEventCatalogFeed(options: {
  forceRefresh?: boolean
  category?: PredictionCategory | null
  query?: string
  page?: number
} = {}): Promise<HomeEventFeed> {
  const manifest = await loadEventCatalogManifest({ forceRefresh: options.forceRefresh })
  const category = options.category ?? null
  const query = options.query?.trim() ?? ''
  const page = options.page ?? 0
  let events: PredictionEventCatalog[]
  let hasMore = false
  if (query) {
    events = await searchEvents(query, { forceRefresh: options.forceRefresh })
  } else if (category) {
    const result = await loadEventCategoryPage(category, page, { forceRefresh: options.forceRefresh })
    events = result.events
    hasMore = result.hasMore
  } else {
    events = await loadFeaturedEvents({ forceRefresh: options.forceRefresh })
  }
  const enabledEvents = events.filter((event) => event.sources.some((source) => sourceEnabled(sourceIdForName(source))))
  const sources = SOURCE_IDS.map((id) => makeSourceResult(
    id === 'polymarket' ? 'Polymarket' : 'Kalshi',
    enabledEvents.filter((event) => event.sources.some((source) => source.toLowerCase() === id)),
    manifest,
  ))
  return {
    events: [...enabledEvents].sort((left, right) => activityScore(right) - activityScore(left)),
    sources,
    fetchedAt: manifest.generatedAt,
    hasMore,
    nextPage: hasMore ? page + 1 : page,
  }
}

export function resetEventCache() {
  manifestPromise = null
  featuredPromise = null
  searchPromise = null
  resolvedCategoryIndexPromise = null
  chunkPromises.clear()
  rawChunkPromises.clear()
}
